import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, UploadCloud, X, Loader2, Save, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Plus, Tag, ImagePlus, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const ALBUM = 'ward8';
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_MB = 25;

const titleFromFile = (name) => (name || 'Photo').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 120) || 'Photo';

/* ---------- Community tag picker (shared by uploader + existing photos) ---------- */
const TagPicker = ({ value = [], onChange, suggestions = [], testid }) => {
  const [custom, setCustom] = useState('');
  const toggle = (t) => onChange(value.includes(t) ? value.filter((v) => v !== t) : [...value, t]);
  const addCustom = () => {
    const t = custom.trim().slice(0, 60);
    if (!t) return;
    if (!value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setCustom('');
  };
  const all = [...suggestions, ...value.filter((v) => !suggestions.includes(v))];
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {all.map((t) => {
          const on = value.includes(t);
          return (
            <button key={t} type="button" onClick={() => toggle(t)} aria-pressed={on} data-testid={`${testid}-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 min-h-[32px] rounded-full text-xs font-bold border transition-colors duration-150 ${on ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#C9A84C]'}`}>
              <Tag className="h-3 w-3" /> {t}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} placeholder="Add another community or place (e.g. Pigeon Lake)" className="h-9 text-sm" data-testid={`${testid}-custom-input`} />
        <Button type="button" variant="outline" onClick={addCustom} className="h-9 px-3" data-testid={`${testid}-custom-add`}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

/* ---------- One queued upload ---------- */
const QueuedPhoto = ({ q, suggestions, onChange, onRemove }) => (
  <Card className={`overflow-hidden ${q.status === 'error' ? 'border-red-300' : q.status === 'done' ? 'border-emerald-300' : ''}`} data-testid={`ward8-queue-item-${q.key}`}>
    <div className="flex flex-col sm:flex-row">
      <div className="sm:w-56 shrink-0 bg-slate-100 relative">
        <img src={q.preview} alt="" className="h-44 sm:h-full w-full object-cover" />
        {q.status === 'uploading' && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="h-6 w-6 text-white animate-spin" /></div>}
        {q.status === 'done' && <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full"><CheckCircle2 className="h-3 w-3" /> Uploaded</span>}
        {q.status === 'error' && <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-red-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full"><AlertCircle className="h-3 w-3" /> Failed</span>}
      </div>
      <CardContent className="p-4 flex-1 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs text-slate-500 truncate">{q.file.name} · {(q.file.size / 1024 / 1024).toFixed(1)} MB</p>
          {q.status !== 'uploading' && <button type="button" onClick={onRemove} aria-label="Remove from queue" className="text-slate-400 hover:text-red-600 p-1" data-testid={`ward8-queue-remove-${q.key}`}><X className="h-4 w-4" /></button>}
        </div>
        {q.error && <p className="text-xs text-red-700 font-semibold" data-testid={`ward8-queue-error-${q.key}`}>{q.error}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs font-bold text-slate-600">Title</Label><Input value={q.title} onChange={(e) => onChange({ title: e.target.value })} disabled={q.status === 'done'} className="h-10 mt-1" data-testid={`ward8-queue-title-${q.key}`} /></div>
          <div><Label className="text-xs font-bold text-slate-600">Photo credit (optional)</Label><Input value={q.photo_credit} onChange={(e) => onChange({ photo_credit: e.target.value })} disabled={q.status === 'done'} placeholder="e.g. Photo: Steve Lesperance" className="h-10 mt-1" data-testid={`ward8-queue-credit-${q.key}`} /></div>
        </div>
        <div><Label className="text-xs font-bold text-slate-600">Caption</Label><Textarea value={q.caption} onChange={(e) => onChange({ caption: e.target.value })} disabled={q.status === 'done'} rows={2} placeholder="Where and when this was taken, and what makes it Ward 8." className="mt-1" data-testid={`ward8-queue-caption-${q.key}`} /></div>
        <div><Label className="text-xs font-bold text-slate-600">Community tags</Label><div className="mt-1"><TagPicker value={q.communities} onChange={(v) => onChange({ communities: v })} suggestions={suggestions} testid={`ward8-queue-tags-${q.key}`} /></div></div>
        <label className="flex items-center gap-2 text-xs font-bold text-slate-600">Publish right away <Switch checked={q.published} onCheckedChange={(v) => onChange({ published: v })} disabled={q.status === 'done'} data-testid={`ward8-queue-published-${q.key}`} /></label>
      </CardContent>
    </div>
  </Card>
);

/* ---------- Existing photo (inline edit) ---------- */
const ExistingPhoto = ({ item, suggestions, onSaved, onDeleted, onMove, isFirst, isLast }) => {
  const [form, setForm] = useState({ title: item.title || '', caption: item.caption || '', communities: item.communities || [], photo_credit: item.photo_credit || '', published: item.published !== false });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify({ title: item.title || '', caption: item.caption || '', communities: item.communities || [], photo_credit: item.photo_credit || '', published: item.published !== false });
  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const r = await api.put(`/gallery/${item.id}`, { ...form, ...patch });
      onSaved(r.data);
      toast.success('Photo updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };
  const del = async () => {
    try {
      await api.delete(`/gallery/${item.id}`);
      onDeleted(item.id);
      toast.success('Photo removed from the album');
    } catch (e) { toast.error('Could not delete'); }
  };
  return (
    <Card className="overflow-hidden" data-testid={`ward8-photo-${item.id}`}>
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-56 shrink-0 bg-slate-100 relative">
          <img src={fileUrl(item.image_url)} alt={item.title} className="h-44 sm:h-full w-full object-cover" loading="lazy" />
          {!form.published && <span className="absolute top-2 left-2 bg-slate-800 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">Hidden</span>}
        </div>
        <CardContent className="p-4 flex-1 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-10" placeholder="Title" data-testid={`ward8-photo-title-${item.id}`} />
            <Input value={form.photo_credit} onChange={(e) => setForm({ ...form, photo_credit: e.target.value })} className="h-10" placeholder="Photo credit (optional)" data-testid={`ward8-photo-credit-${item.id}`} />
          </div>
          <Textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={2} placeholder="Caption" data-testid={`ward8-photo-caption-${item.id}`} />
          <TagPicker value={form.communities} onChange={(v) => setForm({ ...form, communities: v })} suggestions={suggestions} testid={`ward8-photo-tags-${item.id}`} />
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" onClick={() => save()} disabled={saving || !dirty} className="h-9 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid={`ward8-photo-save-${item.id}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
            <Button size="sm" variant="outline" onClick={() => { const v = !form.published; setForm({ ...form, published: v }); save({ published: v }); }} className="h-9" data-testid={`ward8-photo-toggle-${item.id}`}>{form.published ? <><EyeOff className="h-4 w-4 mr-1" /> Hide</> : <><Eye className="h-4 w-4 mr-1" /> Publish</>}</Button>
            <div className="ml-auto flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => onMove(item, -1)} disabled={isFirst} aria-label="Move up" className="h-9 w-9" data-testid={`ward8-photo-up-${item.id}`}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => onMove(item, 1)} disabled={isLast} aria-label="Move down" className="h-9 w-9" data-testid={`ward8-photo-down-${item.id}`}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete photo" className="h-9 w-9 text-red-600 hover:text-red-700" data-testid={`ward8-photo-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </div>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this photo from the album?</AlertDialogTitle><AlertDialogDescription>"{item.title}" will no longer appear in Beautiful Ward 8. The uploaded file stays in the Media Library.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid={`ward8-photo-delete-confirm-${item.id}`}>Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

/* ---------- Page ---------- */
export default function AdminWard8Photos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([api.get('/admin/gallery'), api.get('/gallery/communities', { params: { category: ALBUM } })]);
      setItems((g.data || []).filter((i) => i.category === ALBUM).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
      const cfg = c.data?.configured || [];
      const used = (c.data?.in_use || []).filter((t) => !cfg.includes(t));
      setSuggestions([...cfg, ...used]);
    } catch (e) {
      toast.error('Could not load the album');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addFiles = (files) => {
    const list = Array.from(files || []);
    const next = [];
    list.forEach((file) => {
      if (!file.type.startsWith('image/')) { toast.error(`${file.name}: only image files can be added`); return; }
      if (file.size > MAX_MB * 1024 * 1024) { toast.error(`${file.name}: larger than ${MAX_MB} MB`); return; }
      next.push({ key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, preview: URL.createObjectURL(file), title: titleFromFile(file.name), caption: '', photo_credit: '', communities: [], published: true, status: 'ready', error: '' });
    });
    if (next.length) setQueue((q) => [...q, ...next]);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const updateQ = (key, patch) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const removeQ = (key) => setQueue((q) => { const x = q.find((i) => i.key === key); if (x) URL.revokeObjectURL(x.preview); return q.filter((i) => i.key !== key); });

  const uploadAll = async () => {
    const pending = queue.filter((q) => q.status === 'ready' || q.status === 'error');
    if (!pending.length) return;
    setUploading(true);
    let ok = 0;
    for (const q of pending) {
      updateQ(q.key, { status: 'uploading', error: '' });
      const fd = new FormData();
      fd.append('file', q.file);
      fd.append('title', q.title);
      fd.append('caption', q.caption);
      fd.append('photo_credit', q.photo_credit);
      fd.append('category', ALBUM);
      fd.append('communities', q.communities.join(','));
      fd.append('published', q.published ? 'true' : 'false');
      try {
        const r = await api.post('/admin/gallery/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        updateQ(q.key, { status: 'done' });
        setItems((prev) => [...prev, r.data]);
        ok += 1;
      } catch (e) {
        updateQ(q.key, { status: 'error', error: e?.response?.data?.detail || 'Upload failed. Check the connection and try again.' });
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} photo${ok === 1 ? '' : 's'} added to Beautiful Ward 8`);
    load();
  };

  const clearDone = () => setQueue((q) => { q.filter((x) => x.status === 'done').forEach((x) => URL.revokeObjectURL(x.preview)); return q.filter((x) => x.status !== 'done'); });

  const move = async (item, dir) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx]; const b = items[j];
    const next = [...items]; next[idx] = b; next[j] = a;
    const renumbered = next.map((it, n) => ({ ...it, order: n + 1 }));
    setItems(renumbered);
    try {
      await Promise.all([api.put(`/gallery/${a.id}`, { order: j + 1 }), api.put(`/gallery/${b.id}`, { order: idx + 1 })]);
    } catch (e) { toast.error('Could not reorder'); load(); }
  };

  const readyCount = queue.filter((q) => q.status === 'ready' || q.status === 'error').length;
  const doneCount = queue.filter((q) => q.status === 'done').length;
  const publishedCount = items.filter((i) => i.published !== false).length;

  return (
    <div className="space-y-6" data-testid="admin-ward8-photos-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif font-bold text-[#1a2744] flex items-center gap-2"><Camera className="h-6 w-6" /> Beautiful Ward 8 Photos</h1>
          <p className="text-sm text-slate-600 mt-1">Drop photos in, add a caption and the community they show, and they appear under Media &gt; Photos &gt; Beautiful Ward 8 (<a href="/gallery?album=ward8" target="_blank" rel="noopener noreferrer" className="underline decoration-[#C9A84C] decoration-2 underline-offset-2 font-semibold">open the public album</a>).</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="ward8-album-counts"><Badge variant="outline">{items.length} in album</Badge><Badge variant="outline">{publishedCount} published</Badge></div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
        role="button" tabIndex={0} aria-label="Add photos: drop files here or press to choose"
        className={`rounded-2xl border-2 border-dashed p-8 sm:p-10 text-center cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C] ${dragging ? 'border-[#C9A84C] bg-[#FBF7EE]' : 'border-slate-300 bg-white hover:border-[#1a2744]/50 hover:bg-[#F6F8FB]'}`}
        data-testid="ward8-dropzone"
      >
        <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="ward8-file-input" />
        <div className="mx-auto h-14 w-14 rounded-2xl bg-[#1a2744] flex items-center justify-center mb-3"><UploadCloud className="h-7 w-7 text-[#C9A84C]" /></div>
        <p className="font-serif font-bold text-[#1a2744] text-lg">Drop photos here, or click to choose</p>
        <p className="text-sm text-slate-600 mt-1">JPG, PNG, WEBP or GIF, up to {MAX_MB} MB each. You can add several at once, then caption and tag each one before uploading.</p>
      </div>

      {/* Queue */}
      {queue.length > 0 && (
        <section className="space-y-3" data-testid="ward8-queue">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-[#1a2744] flex items-center gap-2"><ImagePlus className="h-4 w-4" /> {queue.length} photo{queue.length === 1 ? '' : 's'} ready to caption and tag</p>
            <div className="flex items-center gap-2">
              {doneCount > 0 && <Button variant="ghost" onClick={clearDone} className="h-10" data-testid="ward8-clear-done">Clear uploaded</Button>}
              <Button onClick={uploadAll} disabled={uploading || readyCount === 0} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="ward8-upload-all">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />} Upload {readyCount > 0 ? readyCount : ''} to the album
              </Button>
            </div>
          </div>
          {queue.map((q) => <QueuedPhoto key={q.key} q={q} suggestions={suggestions} onChange={(patch) => updateQ(q.key, patch)} onRemove={() => removeQ(q.key)} />)}
        </section>
      )}

      {/* Existing */}
      <section className="space-y-3" data-testid="ward8-existing">
        <h2 className="font-serif font-bold text-[#1a2744] text-lg">In the album</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="h-5 w-5 animate-spin" /> Loading photos</div>
        ) : items.length === 0 ? (
          <Alert className="bg-[#F6F8FB] border-slate-200"><AlertDescription className="text-slate-700">No photos in Beautiful Ward 8 yet. Drop the first ones in above.</AlertDescription></Alert>
        ) : (
          items.map((item, i) => (
            <ExistingPhoto key={item.id} item={item} suggestions={suggestions} isFirst={i === 0} isLast={i === items.length - 1}
              onSaved={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
              onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
              onMove={move} />
          ))
        )}
      </section>
    </div>
  );
}
